import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  MenuItem,
  InputBase,
  IconButton,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid } from '@mui/x-data-grid';
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  orderBy,
  limit,
  doc,
  deleteDoc,
} from 'firebase/firestore';

const ANALYSIS_ENDPOINT = 'https://reporting-exec-analysis-795950092325.europe-west3.run.app';

const DATE_FORMATTER = typeof Intl !== 'undefined'
  ? new Intl.DateTimeFormat('tr-TR', { dateStyle: 'short', timeStyle: 'short' })
  : null;

const formatTimestamp = (date) => {
  if (!date || !DATE_FORMATTER) return '';
  try {
    return DATE_FORMATTER.format(date);
  } catch (err) {
    return '';
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
};

const sanitizeForFirestore = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForFirestore(item));
  }
  if (value && typeof value === 'object') {
    const clone = {};
    Object.entries(value).forEach(([key, val]) => {
      if (val === undefined) return;
      const sanitized = sanitizeForFirestore(val);
      clone[key] = sanitized;
    });
    return clone;
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return null;
  }
  if (value === undefined) {
    return null;
  }
  return value;
};

const isRowObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value);

const normalizeApiTable = (tableLike) => {
  if (!tableLike || typeof tableLike !== 'object') return null;

  const rawColumns = Array.isArray(tableLike.columns) ? tableLike.columns : [];
  const columns = rawColumns.map((col, idx) => {
    if (typeof col === 'string') {
      const field = col;
      return {
        field,
        headerName: col,
        flex: 1,
        minWidth: Math.min(260, Math.max(140, col.length * 10)),
      };
    }
    const rawId = col?.id ?? col?.field ?? `col_${idx}`;
    const header =
      col?.label ??
      col?.header ??
      col?.headerName ??
      col?.name ??
      String(rawId);
    const field = String(rawId);
    return {
      field,
      headerName: header,
      flex: 1,
      minWidth: Math.min(260, Math.max(140, header.length * 10)),
    };
  });

  const rawRows = Array.isArray(tableLike.rows) ? tableLike.rows : [];
  const rows = rawRows.map((row, idx) => {
    if (row && typeof row === 'object') {
      const rowId =
        row.id ??
        row._id ??
        row.__rowId ??
        row.__rowid ??
        row.__row_id ??
        row.cluster ??
        idx;
      return { id: rowId, ...row };
    }
    return { id: idx, value: formatValue(row) };
  });

  if (!columns.length || !rows.length) {
    return null;
  }

  return { columns, rows };
};

const extractFirstTable = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  if (payload.table && typeof payload.table === 'object') {
    return {
      table: payload.table,
      title: payload.title || payload.name || payload.table?.title || payload.table?.name || '',
    };
  }

  if (Array.isArray(payload.tables) && payload.tables.length) {
    const first = payload.tables[0];
    if (!first) return null;
    if (first.table) {
      return {
        table: first.table,
        title: first.title || first.name || first.table?.title || first.table?.name || '',
      };
    }
    return {
      table: first,
      title: first.title || first.name || '',
    };
  }

  if (Array.isArray(payload.tableList) && payload.tableList.length) {
    const first = payload.tableList[0];
    if (!first) return null;
    return {
      table: first.table || first,
      title: first.title || first.name || '',
    };
  }

  return null;
};

const buildGridFromResponse = (rawPayload) => {
  let payload = rawPayload;

  if (payload && typeof payload === 'object' && payload.result) {
    payload = payload.result;
  }

  const extracted = extractFirstTable(payload);
  if (extracted) {
    const normalized = normalizeApiTable(extracted.table);
    if (normalized) {
      const summary = `Tablo ${normalized.rows.length} satır içeriyor.`;
      return {
        columns: normalized.columns,
        rows: normalized.rows,
        summary,
        title: extracted.title || '',
      };
    }
  }

  if (Array.isArray(payload)) {
    if (payload.length === 0) {
      return {
        columns: [],
        rows: [],
        summary: 'Boş sonuç döndü.',
      };
    }

    if (payload.every((item) => isRowObject(item))) {
      const fields = new Set();
      payload.forEach((item) => {
        Object.keys(item || {}).forEach((key) => fields.add(key));
      });
      const headers = Array.from(fields);
      const columns = headers.map((field) => ({
        field,
        headerName: field,
        flex: 1,
        minWidth: 140,
      }));
      const rows = payload.map((item, idx) => {
        const rowId = item?.id ?? item?._id ?? idx;
        const row = { id: rowId };
        headers.forEach((field) => {
          row[field] = formatValue(item ? item[field] : null);
        });
        return row;
      });
      return {
        columns,
        rows,
        summary: `Tablo ${rows.length} satır içeriyor.`,
      };
    }

    const rows = payload.map((item, idx) => ({
      id: idx,
      value: formatValue(item),
    }));
    return {
      columns: [
        { field: 'value', headerName: 'Değer', flex: 1, minWidth: 200 },
      ],
      rows,
      summary: `Liste ${rows.length} değer içeriyor.`,
    };
  }

  if (payload && typeof payload === 'object') {
    const rows = Object.entries(payload).map(([key, value], index) => ({
      id: key || index,
      key,
      value: formatValue(value),
    }));
    return {
      columns: [
        { field: 'key', headerName: 'Alan', minWidth: 160 },
        { field: 'value', headerName: 'Değer', flex: 1, minWidth: 220 },
      ],
      rows,
      summary: `JSON ${rows.length} alan içeriyor.`,
    };
  }

  if (payload === null || payload === undefined) {
    return {
      columns: [],
      rows: [],
      summary: 'Yanıt boş geldi.',
    };
  }

  return {
    columns: [{ field: 'value', headerName: 'Yanıt', flex: 1, minWidth: 200 }],
    rows: [{ id: 0, value: formatValue(payload) }],
    summary: 'Tek değer döndü.',
  };
};

const summarizeResponse = (payload) => {
  const stdoutText = (() => {
    if (!payload || typeof payload !== 'object') return '';
    const stdout = typeof payload.stdout === 'string' && payload.stdout.trim()
      ? payload.stdout.trim()
      : (payload.result && typeof payload.result.stdout === 'string'
          ? payload.result.stdout.trim()
          : '');
    return stdout;
  })();

  let target = payload;
  if (target && typeof target === 'object' && target.result) {
    target = target.result;
  }

  const extracted = extractFirstTable(target);
  if (extracted) {
    const columns = Array.isArray(extracted.table?.columns) ? extracted.table.columns.length : 0;
    const rows = Array.isArray(extracted.table?.rows) ? extracted.table.rows.length : 0;
    const name = extracted.title ? ` (${extracted.title})` : '';
    const base = `Tablo${name} ${rows} satır / ${columns} sütun içeriyor.`;
    return stdoutText ? `${base} | stdout: ${stdoutText.split('\n')[0]}` : base;
  }

  if (Array.isArray(target)) {
    if (target.length === 0) return 'Boş sonuç döndü.';
    if (target.every((item) => isRowObject(item))) {
      const columnCount = target.reduce((set, item) => {
        Object.keys(item || {}).forEach((key) => set.add(key));
        return set;
      }, new Set()).size;
      return `Tablo ${target.length} satır / ${columnCount} sütun içeriyor.`;
    }
    const base = `Liste ${target.length} değer içeriyor.`;
    return stdoutText ? `${base} | stdout: ${stdoutText.split('\n')[0]}` : base;
  }
  if (target && typeof target === 'object') {
    const fieldCount = Object.keys(target).length;
    const base = `JSON ${fieldCount} alan içeriyor.`;
    return stdoutText ? `${base} | stdout: ${stdoutText.split('\n')[0]}` : base;
  }
  if (target === null || target === undefined) {
    return stdoutText ? `Stdout: ${stdoutText.split('\n')[0]}` : 'Yanıt boş geldi.';
  }
  const base = formatValue(target);
  return stdoutText ? `${base} | stdout: ${stdoutText.split('\n')[0]}` : base;
};

const FIRESTORE_DOC_LIMIT = 1024 * 1024;
const FIRESTORE_SAFETY_BUFFER = 120 * 1024;
const TARGET_DOC_LIMIT = FIRESTORE_DOC_LIMIT - FIRESTORE_SAFETY_BUFFER;
const MAX_STORED_ROWS = 200;
const MAX_STORED_ARRAY_ITEMS = 200;
const MAX_STORED_STRING_LENGTH = 8000;

const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (err) {
    return null;
  }
};

const truncateStringValue = (value, limit = MAX_STORED_STRING_LENGTH) => {
  if (typeof value !== 'string' || value.length <= limit) return value;
  const omitted = value.length - limit;
  return `${value.slice(0, limit)}… (+${omitted.toLocaleString('tr-TR')} karakter)`;
};

const truncateValueForStorage = (value, path = []) => {
  if (value === null || typeof value !== 'object') {
    return typeof value === 'string' ? truncateStringValue(value) : value;
  }

  if (Array.isArray(value)) {
    const parentKey = path[path.length - 1];
    const isRowArray = parentKey === 'rows' || parentKey === 'data';
    const limit = isRowArray ? MAX_STORED_ROWS : MAX_STORED_ARRAY_ITEMS;
    const truncated = value.slice(0, limit).map((item, index) =>
      truncateValueForStorage(item, path.concat(index))
    );
    if (value.length > limit) {
      truncated.push({
        truncatedMeta: true,
        omittedCount: value.length - limit,
        message: `${value.length - limit} ek kayıt saklanmadı.`,
      });
    }
    return truncated;
  }

  const next = {};
  Object.entries(value).forEach(([key, val]) => {
    next[key] = truncateValueForStorage(val, path.concat(key));
  });
  return next;
};

const buildFallbackResponse = (response) => {
  const preview = buildGridFromResponse(response);
  const rows = Array.isArray(preview?.rows)
    ? preview.rows.slice(0, MAX_STORED_ROWS)
    : [];
  const stdout =
    (typeof response?.stdout === 'string' && response.stdout) ||
    (response?.result && typeof response.result.stdout === 'string' && response.result.stdout) ||
    '';
  return {
    truncated: true,
    summary: preview?.summary || summarizeResponse(response),
    stdout: truncateStringValue(stdout),
    table: preview
      ? {
          columns: preview.columns,
          rows,
          title: preview.title || '',
        }
      : null,
  };
};

const prepareResponseForStorage = (response) => {
  const sanitized = sanitizeForFirestore(response);
  const initialPayload = safeJsonStringify(sanitized);
  if (initialPayload && initialPayload.length <= TARGET_DOC_LIMIT) {
    return sanitized;
  }

  const truncatedPayload = truncateValueForStorage(sanitized);
  const truncatedString = safeJsonStringify(truncatedPayload);
  if (truncatedString && truncatedString.length <= TARGET_DOC_LIMIT) {
    return truncatedPayload;
  }

  return sanitizeForFirestore(buildFallbackResponse(response));
};

const MessageBubble = ({
  role,
  content,
  timestamp,
  pending = false,
  onSelect,
  onDelete,
  active = false,
  variables = [],
  actionsDisabled = false,
}) => {
  const normalizedVariables = Array.isArray(variables)
    ? variables
        .map((item, idx) => {
          if (!item) return null;
          if (typeof item === 'string') {
            return { id: item, label: item };
          }
          const id = item.id ?? item.value ?? `var_${idx}`;
          const label = item.label ?? item.header ?? id;
          if (!id && !label) return null;
          return { id: id || `var_${idx}`, label: label || id };
        })
        .filter(Boolean)
    : [];

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 1.25,
          borderRadius: 3,
          maxWidth: '85%',
          border: '1px solid rgba(148, 163, 184, 0.24)',
          backgroundColor:
            role === 'user'
              ? 'rgba(96, 165, 250, 0.16)'
              : active
                ? 'rgba(96, 165, 250, 0.24)'
                : 'rgba(248, 250, 252, 0.9)',
        }}
      >
        <Stack spacing={0.75}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {role === 'user' ? 'Siz' : 'Analiz'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </Typography>
          {normalizedVariables.length ? (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.25 }}
            >
              {normalizedVariables.map((variable, index) => (
                <Chip
                  key={`${variable.id}-${index}`}
                  size="small"
                  variant="outlined"
                  label={variable.label || variable.id}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Stack>
          ) : null}
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            {pending ? (
              role === 'assistant' ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Analiz çalıştırılıyor...
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Gönderiliyor...
                </Typography>
              )
            ) : (
              <Typography variant="caption" color="text.secondary">
                {formatTimestamp(timestamp)}
              </Typography>
            )}
            {role === 'assistant' &&
            !pending &&
            (typeof onSelect === 'function' || typeof onDelete === 'function') ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                {typeof onSelect === 'function' ? (
                  <Button
                    size="small"
                    variant={active ? 'contained' : 'outlined'}
                    onClick={onSelect}
                    disabled={actionsDisabled}
                  >
                    Tabloyu Aç
                  </Button>
                ) : null}
                {typeof onDelete === 'function' ? (
                  <Tooltip title="Kaydı sil" arrow>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={onDelete}
                        disabled={actionsDisabled}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

const SectionCard = ({ title, subtitle, icon = null, actions = null, sx = {}, children }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: '1px solid rgba(148, 163, 184, 0.28)',
      backgroundColor: '#ffffff',
      p: { xs: 2, md: 2.5 },
      ...sx,
    }}
  >
    <Stack spacing={2} sx={{ height: '100%' }}>
      {(title || subtitle || icon || actions) ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            {icon ? (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(14,165,233,0.12))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              {title ? (
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                  {title}
                </Typography>
              ) : null}
              {subtitle ? (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          {actions ? (
            <Box
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              {actions}
            </Box>
          ) : null}
        </Stack>
      ) : null}
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Stack>
  </Paper>
);

const StatPill = ({ label, value }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 2,
      border: '1px solid rgba(148, 163, 184, 0.32)',
      backgroundColor: 'rgba(248, 250, 252, 0.9)',
      minWidth: 140,
    }}
  >
    <Stack spacing={0.25}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ letterSpacing: 0.6, textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Stack>
  </Box>
);

export default function AnalysisCard({
  db,
  organisation,
  projectID,
  analysisID,
  currentUser,
  builderInputs = null,
  pivotOptions = null,
}) {
  const [goalInput, setGoalInput] = useState('');
  const [pendingGoal, setPendingGoal] = useState('');
  const [history, setHistory] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [variableMenuAnchor, setVariableMenuAnchor] = useState(null);
  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const goalInputRef = useRef(null);
  const caretPositionRef = useRef({ start: 0, end: 0 });

  const normalizedInputs = useMemo(() => {
    const payload = builderInputs && typeof builderInputs === 'object' ? builderInputs : {};
    const multiquestion =
      (Array.isArray(payload.multiquestion) ? payload.multiquestion : null) ||
      (Array.isArray(payload.multiquestions) ? payload.multiquestions : null) ||
      [];
    const columnTypes =
      (Array.isArray(payload.column_types) ? payload.column_types : null) ||
      (Array.isArray(payload.columnTypes) ? payload.columnTypes : null) ||
      [];
    const rows = Array.isArray(payload.rows) ? payload.rows : [];
    const filterGroups = Array.isArray(payload.filterGroups) ? payload.filterGroups : [];
    const likertGroups = Array.isArray(payload.likertGroups) ? payload.likertGroups : [];
    const undupeQuestions = Array.isArray(payload.undupeQuestions) ? payload.undupeQuestions : [];
    const idToHeader =
      (payload.id_to_header && typeof payload.id_to_header === 'object')
        ? payload.id_to_header
        : (payload.idToHeader && typeof payload.idToHeader === 'object'
            ? payload.idToHeader
            : {});
    const toHeader =
      (payload.to_header && typeof payload.to_header === 'object')
        ? payload.to_header
        : (payload.toHeader && typeof payload.toHeader === 'object'
            ? payload.toHeader
            : idToHeader);

  return {
    projectID: projectID || payload.projectID || '',
    rows,
    column_types: columnTypes,
    id_to_header: idToHeader,
      to_header: toHeader,
      multiquestion,
      multiquestions: Array.isArray(payload.multiquestions) ? payload.multiquestions : multiquestion,
      filterGroups,
      likertGroups,
    undupeQuestions,
  };
}, [builderInputs, projectID]);

  const columnTypeCatalog = useMemo(() => {
    const list = [];
    const byId = new Map();
    const byHeader = new Map();
    const rawColumns = Array.isArray(normalizedInputs?.column_types)
      ? normalizedInputs.column_types
      : [];

    rawColumns.forEach((col, idx) => {
      if (!col || typeof col !== 'object') return;
      const rawHeader =
        col.header ??
        col.name ??
        col.label ??
        col.text ??
        col.field ??
        col.id ??
        '';
      const rawId = col.id ?? col.field ?? rawHeader ?? `col_${idx}`;
      const id = typeof rawId === 'string' ? rawId.trim() : String(rawId || '').trim();
      const header = typeof rawHeader === 'string' ? rawHeader.trim() : String(rawHeader || '').trim();
      if (!id && !header) return;
      const entryId = id || (header ? `${header}_${idx}` : `col_${idx}`);
      const label = header || id || entryId;
      if (!byId.has(entryId)) {
        const entry = { id: entryId, label, header, raw: col };
        byId.set(entryId, entry);
        list.push(entry);
      }
      const headerKey = header ? header.toLowerCase() : '';
      if (headerKey && !byHeader.has(headerKey)) {
        byHeader.set(headerKey, byId.get(entryId));
      }
    });

    return { list, byId, byHeader };
  }, [normalizedInputs]);

  const availableVariables = useMemo(() => {
    const dedupe = new Map();
    const normalizeId = (value) => {
      if (value == null) return '';
      if (typeof value === 'string') return value.trim();
      return String(value).trim();
    };
    const normalizeLabel = (value) => {
      if (value == null) return '';
      if (typeof value === 'string') return value.trim();
      return String(value).trim();
    };
    const resolveIdFromLabel = (label) => {
      if (!label) return '';
      const hit = columnTypeCatalog.byHeader.get(label.toLowerCase());
      return hit?.id || '';
    };
    const pushVariable = (rawId, rawLabel) => {
      const idCandidate = normalizeId(rawId);
      const labelCandidate = normalizeLabel(rawLabel);
      let id = idCandidate;
      if (!id) {
        id = resolveIdFromLabel(labelCandidate);
      }
      if (!id) return;
      if (dedupe.has(id)) return;
      const catalogEntry = columnTypeCatalog.byId.get(id);
      const label = labelCandidate || catalogEntry?.label || id;
      dedupe.set(id, { id, label });
    };

    const displayCols = Array.isArray(pivotOptions?.displayCols) ? pivotOptions.displayCols : [];
    displayCols.forEach((col) => {
      if (!col || typeof col !== 'object') return;
      const baseLabel =
        col.label ??
        col.header ??
        col.name ??
        col.title ??
        col.id ??
        '';
      pushVariable(col.id ?? col.header ?? col.name, baseLabel);
      if (Array.isArray(col.columns)) {
        col.columns.forEach((nested) => {
          if (!nested || typeof nested !== 'object') return;
          const nestedLabel =
            nested.label ??
            nested.header ??
            nested.name ??
            nested.title ??
            nested.id ??
            '';
          pushVariable(nested.id ?? nested.header ?? nested.name, nestedLabel);
        });
      }
    });

    const leafColumns = Array.isArray(pivotOptions?.leafColumnOptions)
      ? pivotOptions.leafColumnOptions
      : [];
    leafColumns.forEach((col) => {
      if (!col) return;
      const label =
        col.label ??
        col.header ??
        col.name ??
        col.title ??
        col.id ??
        '';
      pushVariable(col.id ?? col.header ?? col.name, label);
    });

    const rowGroups = Array.isArray(pivotOptions?.rowGroupOptions)
      ? pivotOptions.rowGroupOptions
      : [];
    rowGroups.forEach((label) => {
      const str = normalizeLabel(label);
      if (!str) return;
      pushVariable('', str);
    });

    columnTypeCatalog.list.forEach((col) => {
      pushVariable(col.id, col.label);
    });

    return Array.from(dedupe.values());
  }, [columnTypeCatalog, pivotOptions]);

  const variableMap = useMemo(() => {
    const map = new Map();
    availableVariables.forEach((item) => {
      if (!item?.id) return;
      map.set(item.id, item);
    });
    return map;
  }, [availableVariables]);

  const variableLookupMap = useMemo(() => {
    const map = new Map();
    availableVariables.forEach((item) => {
      if (!item) return;
      if (item.id) {
        map.set(item.id.toLowerCase(), item);
      }
      if (item.label) {
        map.set(item.label.toLowerCase(), item);
      }
    });
    return map;
  }, [availableVariables]);

  const selectedVariables = useMemo(() => {
    if (!goalInput) return [];
    const matches = [];
    const regex = /\{\{([^{}]+)\}\}/g;
    let match = regex.exec(goalInput);
    while (match) {
      const token = match[1]?.trim()?.toLowerCase();
      if (token && variableLookupMap.has(token)) {
        matches.push(variableLookupMap.get(token));
      }
      match = regex.exec(goalInput);
    }
    return matches;
  }, [goalInput, variableLookupMap]);

  useEffect(() => {
    if (!availableVariables.length && variableMenuAnchor) {
      setVariableMenuAnchor(null);
    }
  }, [availableVariables.length, variableMenuAnchor]);

  const rememberCaretPosition = useCallback((event) => {
    const target = event?.target ?? goalInputRef.current;
    if (!target) return;
    caretPositionRef.current = {
      start: target.selectionStart ?? caretPositionRef.current.start,
      end: target.selectionEnd ?? caretPositionRef.current.end,
    };
  }, []);

  const handleGoalInputChange = useCallback((event) => {
    const { value, selectionStart, selectionEnd } = event.target;
    setGoalInput(value);
    caretPositionRef.current = {
      start: selectionStart ?? value.length,
      end: selectionEnd ?? value.length,
    };
  }, []);

  const openVariableMenu = useCallback(
    (event) => {
      if (isSubmitting || !availableVariables.length) return;
      setVariableMenuAnchor(event.currentTarget);
    },
    [availableVariables.length, isSubmitting]
  );

  const closeVariableMenu = useCallback(() => {
    setVariableMenuAnchor(null);
  }, []);

  const insertVariableToken = useCallback(
    (variableId) => {
      const meta = variableMap.get(variableId);
      if (!meta) {
        closeVariableMenu();
        return;
      }
      const token = `{{${meta.label || meta.id}}}`;
      setGoalInput((prev) => {
        const { start, end } = caretPositionRef.current;
        const safeStart = Math.max(0, Math.min(start ?? prev.length, prev.length));
        const safeEnd = Math.max(safeStart, Math.min(end ?? prev.length, prev.length));
        const nextValue = `${prev.slice(0, safeStart)}${token}${prev.slice(safeEnd)}`;
        const cursor = safeStart + token.length;
        requestAnimationFrame(() => {
          const inputEl = goalInputRef.current;
          if (inputEl?.focus) {
            inputEl.focus();
            inputEl.setSelectionRange(cursor, cursor);
            caretPositionRef.current = { start: cursor, end: cursor };
          }
        });
        return nextValue;
      });
      closeVariableMenu();
    },
    [closeVariableMenu, variableMap]
  );

  const chatsCollection = useMemo(() => {
    if (!db || !organisation || !projectID) return null;
    const segments = analysisID
      ? ['organisations', organisation, 'analyses', analysisID, 'analysisChats']
      : ['organisations', organisation, 'projects', projectID, 'analysisChats'];
    return collection(db, ...segments);
  }, [analysisID, db, organisation, projectID]);

  const loadHistory = useCallback(async () => {
    if (!chatsCollection) {
      setHistory([]);
      setSelectedId('');
      return;
    }
    setHistoryLoading(true);
    try {
      const snapshot = await getDocs(
        query(chatsCollection, orderBy('createdAt', 'desc'), limit(25))
      );
      const items = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        return {
          id: docSnap.id,
          goal: data.goal || '',
          response: data.response ?? null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
          userEmail: data.userEmail || '',
          inputs: data.inputs || null,
          variables: Array.isArray(data.variables) ? data.variables : [],
          variableLabels: Array.isArray(data.variableLabels) ? data.variableLabels : [],
        };
      });
      setHistory(items);
      setSelectedId((prev) => {
        if (prev && items.some((item) => item.id === prev)) {
          return prev;
        }
        return items[0]?.id || '';
      });
    } catch (err) {
      console.error('Failed to load analysis history', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [chatsCollection]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const persistResult = useCallback(async ({ goal, response, inputs, variables = [] }) => {
    if (!chatsCollection) {
      throw new Error('Kayıt için Firestore koleksiyonu bulunamadı.');
    }
    const normalizedVariables = Array.isArray(variables)
      ? variables
          .map((item, idx) => {
            if (!item) return null;
            if (typeof item === 'string') {
              return { id: item, label: item };
            }
            const id = item.id ?? item.value ?? `var_${idx}`;
            const label = item.label ?? item.header ?? id;
            if (!id && !label) return null;
            return { id: id || `var_${idx}`, label: label || id || `Değişken ${idx + 1}` };
          })
          .filter(Boolean)
      : [];
    const docRef = await addDoc(chatsCollection, {
      goal,
      response: prepareResponseForStorage(response),
      inputs: sanitizeForFirestore(inputs),
      projectID,
      analysisID: analysisID || null,
      userId: currentUser?.uid || null,
      userEmail: currentUser?.email || null,
      variables: normalizedVariables.map((item) => item.id),
      variableLabels: sanitizeForFirestore(normalizedVariables),
      createdAt: serverTimestamp(),
    });
    return docRef;
  }, [analysisID, chatsCollection, currentUser?.email, currentUser?.uid, projectID]);

  const handleDeleteEntry = useCallback(
    async (entryId) => {
      if (!entryId || !chatsCollection) return;
      const confirmed =
        typeof window === 'undefined'
          ? true
          : window.confirm('Bu kaydı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.');
      if (!confirmed) return;
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.add(entryId);
        return next;
      });
      try {
        await deleteDoc(doc(chatsCollection, entryId));
        setHistory((prev) => {
          const nextHistory = prev.filter((item) => item.id !== entryId);
          setSelectedId((current) => {
            if (current === entryId) {
              return nextHistory[0]?.id || '';
            }
            return current;
          });
          return nextHistory;
        });
      } catch (err) {
        console.error('Failed to delete analysis entry', err);
        setErrorMessage(err.message || 'Analiz sonucu silinemedi.');
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(entryId);
          return next;
        });
      }
    },
    [chatsCollection]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedGoal = goalInput.trim();
    if (!trimmedGoal) {
      setErrorMessage('Lütfen analiz için bir hedef girin.');
      return;
    }
    if (!projectID || !organisation) {
      setErrorMessage('Proje veya organizasyon bilgisi yüklenmedi.');
      return;
    }
    if (!chatsCollection) {
      setErrorMessage('Kayıt için Firestore yolu oluşturulamadı.');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);
    setPendingGoal(trimmedGoal);

    try {
      const sanitizedVariables = selectedVariables
        .map((item) => {
          if (!item?.id) return null;
          const meta = variableMap.get(item.id);
          return {
            id: item.id,
            label: meta?.label || item.label || item.id,
          };
        })
        .filter(Boolean);

      const formData = new FormData();
      formData.append('projectID', projectID);
      formData.append('goal', trimmedGoal);
      formData.append('inputs', JSON.stringify(normalizedInputs));
      if (sanitizedVariables.length) {
        formData.append(
          'variables',
          JSON.stringify(sanitizedVariables.map((item) => item.id))
        );
        formData.append('variableLabels', JSON.stringify(sanitizedVariables));
      }

      const response = await fetch(ANALYSIS_ENDPOINT, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const reason = await response.text();
        throw new Error(reason || `API ${response.status} hatası`);
      }
      const rawText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(rawText);
      } catch (err) {
        parsed = rawText;
      }

      const docRef = await persistResult({
        goal: trimmedGoal,
        response: parsed,
        inputs: normalizedInputs,
        variables: sanitizedVariables,
      });
      if (docRef?.id) {
        setSelectedId(docRef.id);
      }
      setGoalInput('');
      setPendingGoal('');
      await loadHistory();
    } catch (err) {
      console.error('Analysis execution failed', err);
      setErrorMessage(err.message || 'Analiz isteği başarısız oldu.');
    } finally {
      setIsSubmitting(false);
      setPendingGoal('');
    }
  };

  const orderedHistory = useMemo(() => {
    const next = [...history];
    next.sort((a, b) => {
      const aTime = a?.createdAt instanceof Date ? a.createdAt.getTime() : 0;
      const bTime = b?.createdAt instanceof Date ? b.createdAt.getTime() : 0;
      return bTime - aTime;
    });
    return next;
  }, [history]);

  const selectedEntry = useMemo(
    () => history.find((item) => item.id === selectedId) || null,
    [history, selectedId]
  );

  const resolveEntryVariables = useCallback(
    (entry) => {
      if (!entry) return [];
      if (Array.isArray(entry.variableLabels) && entry.variableLabels.length) {
        return entry.variableLabels
          .map((item, idx) => {
            if (!item) return null;
            if (typeof item === 'string') {
              const meta = variableMap.get(item);
              return { id: item, label: meta?.label || item };
            }
            const id = item.id ?? item.value ?? `var_${idx}`;
            const fallbackMeta = variableMap.get(item.id ?? item.value);
            const label = item.label ?? fallbackMeta?.label ?? id;
            if (!id && !label) return null;
            return { id: id || `var_${idx}`, label: label || id };
          })
          .filter(Boolean);
      }
      if (Array.isArray(entry.variables) && entry.variables.length) {
        return entry.variables
          .map((id, idx) => {
            if (!id) return null;
            const meta = variableMap.get(id);
            const label = meta?.label || String(id) || `Değişken ${idx + 1}`;
            return { id, label };
          })
          .filter(Boolean);
      }
      return [];
    },
    [variableMap]
  );

  const selectedEntryVariables = useMemo(
    () => resolveEntryVariables(selectedEntry),
    [resolveEntryVariables, selectedEntry]
  );

  const tableData = useMemo(() => {
    const built = buildGridFromResponse(selectedEntry?.response);
    if (!built) {
      return {
        columns: [],
        rows: [],
        summary: '',
        title: '',
      };
    }
    return {
      columns: built.columns || [],
      rows: built.rows || [],
      summary: built.summary || '',
      title: built.title || '',
    };
  }, [selectedEntry]);

  const stdoutOutput = useMemo(() => {
    const response = selectedEntry?.response;
    if (!response || typeof response !== 'object') return '';
    const raw =
      (typeof response.stdout === 'string' && response.stdout.trim()
        ? response.stdout
        : null) ||
      (response.result &&
        typeof response.result === 'object' &&
        typeof response.result.stdout === 'string' &&
        response.result.stdout.trim()
        ? response.result.stdout
        : '');
    return raw ? raw.trim() : '';
  }, [selectedEntry]);

  const isResponseTruncated = Boolean(selectedEntry?.response?.truncated);

  const resultSizeLabel = tableData.rows.length
    ? `${tableData.rows.length} satır • ${tableData.columns.length} sütun`
    : 'Tablo hazır değil';

  return (
    <>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.5} alignItems="stretch">
          <Stack spacing={2.5} sx={{ flex: { xs: 'unset', xl: 0.6 }, minWidth: 0 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <SectionCard
                title="Analiz Hedefiniz"
                subtitle="Metin alanları ve değişken baloncukları aynı cümlede birleşir; API tarafına kimlikleri iletilir."
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                  >

                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={openVariableMenu}
                      disabled={isSubmitting || !availableVariables.length}
                      sx={{
                        borderStyle: selectedVariables.length ? 'dashed' : 'solid',
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        alignSelf: { xs: 'flex-start', sm: 'auto' },
                      }}
                    >
                      Değişken Ekle
                    </Button>
                  </Stack>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px solid rgba(148, 163, 184, 0.45)',
                      backgroundColor: 'rgba(248, 250, 252, 0.9)',
                      px: 1,
                      py: 0.75,
                    }}
                  >
                    <InputBase
                      inputRef={goalInputRef}
                      multiline
                      minRows={3}
                      disabled={isSubmitting}
                      value={goalInput}
                      onChange={handleGoalInputChange}
                      onClick={rememberCaretPosition}
                      onKeyUp={rememberCaretPosition}
                      onMouseUp={rememberCaretPosition}
                      onSelect={rememberCaretPosition}
                      placeholder="Örn. D1 ile D2 arasındaki korelasyon..."
                      sx={{
                        width: '100%',
                        pr: 0.5,
                        pl: 0.5,
                        '& .MuiInputBase-input': {
                          fontSize: '1rem',
                          lineHeight: 1.5,
                          padding: 0,
                          minHeight: 96,
                          resize: 'vertical',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={isSubmitting || !projectID || !organisation}
                    >
                      Gönder
                    </Button>
                  </Box>
                </Stack>
              </SectionCard>
            </Box>
            {errorMessage ? (
              <Alert severity="error" onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            ) : null}
            <SectionCard
              title="Analiz Sonucu"
              subtitle={
                selectedEntry
                  ? 'Seçili kayda ait tabloyu inceleyin.'
                  : 'Sağ taraftan bir kayıt seçtiğinizde sonuçlar burada gösterilecek.'
              }
            >
              <Stack spacing={1.5}>
                <Stack spacing={0.4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    AI Sorusu:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedEntry ? selectedEntry.goal : 'Henüz görüntülenecek sonuç bulunmuyor.'}
                  </Typography>
                </Stack>
                {selectedEntryVariables.length ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {selectedEntryVariables.map((variable, index) => (
                      <Chip
                        key={`${variable.id}-${index}`}
                        size="small"
                        variant="outlined"
                        label={variable.label || variable.id}
                        sx={{ borderRadius: 2 }}
                      />
                    ))}
                  </Stack>
                ) : null}
                {isResponseTruncated ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Yanıt boyutu Firestore sınırını aştığı için kısaltıldı; tablo ve stdout yalnızca
                    ön izleme olarak saklanıyor.
                  </Alert>
                ) : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <StatPill label="Tablo Özeti" value={resultSizeLabel} />
                  <StatPill label="Stdout" value={stdoutOutput ? 'Var' : 'Yok'} />
                </Stack>
                {tableData.summary ? (
                  <Typography variant="caption" color="text.secondary">
                    {tableData.summary}
                  </Typography>
                ) : null}
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid rgba(148, 163, 184, 0.24)',
                    backgroundColor: '#ffffff',
                    p: tableData.columns.length && tableData.rows.length ? 1 : 2,
                  }}
                >
                  {tableData.columns.length > 0 && tableData.rows.length > 0 ? (
                    <DataGrid
                      autoHeight
                      rows={tableData.rows}
                      columns={tableData.columns}
                      disableRowSelectionOnClick
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 10 },
                        },
                      }}
                      sx={{
                        bgcolor: '#ffffff',
                        border: 'none',
                        '& .MuiDataGrid-cell': {
                          whiteSpace: 'normal',
                          lineHeight: '1.4rem',
                        },
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {selectedEntry
                        ? 'Yanıt tablosu oluşturulamadı.'
                        : 'Henüz görüntülenecek sonuç bulunmuyor.'}
                    </Typography>
                  )}
                </Box>
                {stdoutOutput ? (
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: '1px solid rgba(148, 163, 184, 0.24)',
                      backgroundColor: '#ffffff',
                      p: 1.25,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Komut Çıktısı
                    </Typography>
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                        mb: 0,
                      }}
                    >
                      {stdoutOutput}
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            </SectionCard>
          </Stack>

          <SectionCard
            title="Analiz Geçmişi"
            subtitle="En yeni 25 kayıt kronolojik olarak listelenir."
            sx={{ flex: { xs: 'unset', xl: 0.4 }, minWidth: 0 }}
            actions={historyLoading ? <CircularProgress size={18} /> : null}
          >
            <Stack spacing={1.5} sx={{ height: '100%' }}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 320,
                  overflowY: 'auto',
                  pr: { xs: 0, sm: 1 },
                }}
              >
                {historyLoading ? (
                  <Stack alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Kayıtlar yükleniyor...
                    </Typography>
                  </Stack>
                ) : null}
                {orderedHistory.length === 0 && !pendingGoal && !historyLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Henüz kayıtlı analiz bulunmuyor.
                  </Typography>
                ) : null}
                {orderedHistory.map((entry) => {
                  const entryVariables = resolveEntryVariables(entry);
                  return (
                    <Stack key={entry.id} spacing={1.1} sx={{ mb: 1.75 }}>
                      <MessageBubble
                        role="assistant"
                        content={entry.goal || summarizeResponse(entry.response)}
                        timestamp={entry.createdAt}
                        onSelect={() => setSelectedId(entry.id)}
                        active={selectedId === entry.id}
                        variables={entryVariables}
                        onDelete={() => handleDeleteEntry(entry.id)}
                        actionsDisabled={deletingIds.has(entry.id)}
                      />
                    </Stack>
                  );
                })}
              </Box>
            </Stack>
          </SectionCard>
        </Stack>
      </Stack>

      <Menu
        anchorEl={variableMenuAnchor}
        open={Boolean(variableMenuAnchor) && !isSubmitting && availableVariables.length > 0}
        onClose={closeVariableMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { maxHeight: 320 } }}
        disableAutoFocusItem
      >
        {availableVariables.map((variable) => (
          <MenuItem key={variable.id} onClick={() => insertVariableToken(variable.id)}>
            {variable.label}
          </MenuItem>
        ))}
      </Menu>

      {pendingGoal ? (
        <Box
          role="status"
          aria-live="assertive"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.modal + 1,
            backgroundColor: 'rgba(15, 23, 42, 0.72)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            py: 4,
          }}
        >
          <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', maxWidth: 360 }}>
            <CircularProgress size={36} sx={{ color: '#e2e8f0' }} />
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700 }}>
                Analiz çalıştırılıyor
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(226, 232, 240, 0.9)' }}>
                Lütfen bekleyin, sonuçlar hazırlanıyor...
              </Typography>
            </Stack>
          </Stack>
        </Box>
      ) : null}
    </>
  );
}
